import { ROUTES } from "@src/common/constants";
import { useArchiveWhiteboard } from "@src/components/board/mutations/archive-whiteboard";
import { useCloneWhiteboard } from "@src/components/board/mutations/clone-whiteboard";
import { useLeaveCard } from "@src/components/board/mutations/leave-card";
import { useLeaveWhiteboard } from "@src/components/board/mutations/leave-whiteboard";
import { useNavbarRemoveCard } from "@src/components/board/mutations/remove-card-navbar-item";
import { generateNewName } from "@src/components/board/mutations/utils";
import {
	type NavbarBranchItem,
	fetchNavbarBranchQueryOptions,
	findNavbarBranchItemInCache,
	navbarBranchIsSharedOrPublished,
	navbarBranchQueryKeys,
} from "@src/components/board/queries/fetch-ranklist";
import { whiteboardQueryKeys } from "@src/components/board/queries/query-keys";
import type { Whiteboard } from "@src/components/board/schema";
import { useWhiteboardNavigation } from "@src/components/board/use-whiteboard-navigation";
import {
	WhiteboardDocumentId,
	parseWhiteboardDocumentId,
} from "@src/components/collaboration/document-id";
import { openArchiveBlockedModal } from "@src/components/modals/archive-blocked-modal/archive-blocked-modal";
import { NavBarItemType, NavbarSection } from "@src/gql/graphql";
import type { QueryClient } from "@tanstack/react-query";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
	type ReactNode,
	createContext,
	useCallback,
	useContext,
	useMemo,
} from "react";
import { useMatch } from "react-router-dom";
import {
	type NavActiveSource,
	useNavActiveSource,
} from "./nav-active-source-context";

export type NavItemKind = "card" | "board";

interface NavBarHandlerContextType {
	onArchive?: (itemId: string, kind: NavItemKind) => Promise<void>;
	onDuplicate?: (id: string, name: string) => Promise<void>;
	onLeave?: (itemId: string, kind: NavItemKind) => void;
}

const NavBarHandlerContext = createContext<
	NavBarHandlerContextType | undefined
>(undefined);

NavBarHandlerContext.displayName = "NavBarHandlerContext";

function isActiveWhiteboardItem(
	activeWhiteboardId: WhiteboardDocumentId | undefined,
	itemId: string,
): boolean {
	if (!activeWhiteboardId) return false;
	return (
		activeWhiteboardId.toString() === itemId ||
		activeWhiteboardId.toIdString() ===
			parseWhiteboardDocumentId(itemId).toIdString()
	);
}

function listAvailableWhiteboardNanoIds(
	queryClient: QueryClient,
): { id: string }[] {
	const sections = [
		NavbarSection.MyWhiteboards,
		NavbarSection.SharedWithMe,
	] as const;
	const seen = new Set<string>();
	const result: { id: string }[] = [];

	for (const section of sections) {
		const queries = queryClient.getQueriesData<NavbarBranchItem[]>({
			queryKey: navbarBranchQueryKeys.section(section),
		});
		for (const [, data] of queries) {
			if (!data) continue;
			for (const item of data) {
				if (item.type !== NavBarItemType.Board) continue;
				const nanoId = parseWhiteboardDocumentId(item.key).toIdString();
				if (seen.has(nanoId)) continue;
				seen.add(nanoId);
				result.push({ id: nanoId });
			}
		}
	}

	return result;
}

function resolveActiveSourceForWhiteboardNanoId(
	queryClient: QueryClient,
	nanoId: string,
): NavActiveSource {
	const key = new WhiteboardDocumentId(nanoId).toString();
	if (
		findNavbarBranchItemInCache(queryClient, NavbarSection.SharedWithMe, key)
	) {
		return "shared-with-me";
	}
	if (findNavbarBranchItemInCache(queryClient, NavbarSection.Favourites, key)) {
		return "favourites";
	}
	return "my-boards";
}

export function NavBarHandlerProvider({
	children,
}: {
	children: ReactNode;
}) {
	const queryClient = useQueryClient();
	const { setActiveSource } = useNavActiveSource();
	const { data: branchItems } = useQuery(
		fetchNavbarBranchQueryOptions(NavbarSection.MyWhiteboards),
	);

	const activeWhiteboardNames = useMemo(
		() =>
			branchItems
				?.filter((item: NavbarBranchItem) => item.type === NavBarItemType.Board)
				.map((b) => b.name) ?? [],
		[branchItems],
	);

	const { mutateAsync: cloneWhiteboard } = useCloneWhiteboard(false);
	const { mutateAsync: archiveWhiteboard } = useArchiveWhiteboard();
	const { mutateAsync: removeNavbarCard } = useNavbarRemoveCard();
	const { mutate: leaveCard } = useLeaveCard();
	const { mutate: leaveWhiteboard } = useLeaveWhiteboard();
	const { activeWhiteboardId, navigate, navigateBackToWhiteboard } =
		useWhiteboardNavigation();
	const cardRouteMatch = useMatch("/:cardId");

	const navigateAwayFromCurrentBoard = useCallback(
		(excludedItemId: string) => {
			const excludedNanoId =
				parseWhiteboardDocumentId(excludedItemId).toIdString();
			const available = listAvailableWhiteboardNanoIds(queryClient).filter(
				(board) => board.id !== excludedNanoId,
			);
			const destinationId = navigateBackToWhiteboard(available, excludedNanoId);
			if (destinationId) {
				setActiveSource(
					resolveActiveSourceForWhiteboardNanoId(queryClient, destinationId),
				);
			}
		},
		[navigateBackToWhiteboard, queryClient, setActiveSource],
	);

	const handleDuplicateClick = useCallback(
		async (id: string, name: string) => {
			const newName = generateNewName(name, activeWhiteboardNames);

			await cloneWhiteboard({
				id,
				name: newName,
			});
		},
		[activeWhiteboardNames, cloneWhiteboard],
	);

	const handleLeaveClick = useCallback(
		(itemId: string, kind: NavItemKind) => {
			if (kind === "card") {
				leaveCard(itemId, {
					onSuccess: () => {
						if (cardRouteMatch?.params.cardId === itemId) {
							navigate(ROUTES.default);
						}
					},
				});
				return;
			}

			const isCurrentWhiteboard = isActiveWhiteboardItem(
				activeWhiteboardId,
				itemId,
			);
			leaveWhiteboard(itemId, {
				onSuccess: () => {
					if (isCurrentWhiteboard) {
						navigateAwayFromCurrentBoard(itemId);
					}
				},
			});
		},
		[
			activeWhiteboardId,
			cardRouteMatch?.params.cardId,
			leaveCard,
			leaveWhiteboard,
			navigate,
			navigateAwayFromCurrentBoard,
		],
	);

	const handleArchiveClick = useCallback(
		async (itemId: string, kind: NavItemKind) => {
			if (kind === "card") {
				try {
					await removeNavbarCard({
						section: NavbarSection.MyWhiteboards,
						cardId: itemId,
					});
					if (cardRouteMatch?.params.cardId === itemId) {
						navigate(ROUTES.default);
					}
				} catch {
					// Error toast handled by useNavbarRemoveCard.onError
				}
				return;
			}

			const branchItem = findNavbarBranchItemInCache(
				queryClient,
				NavbarSection.MyWhiteboards,
				itemId,
			);

			const whiteboardDocId = parseWhiteboardDocumentId(itemId);
			const nanoId = whiteboardDocId.toIdString();
			const whiteboardCache = queryClient.getQueryData<Whiteboard>(
				whiteboardQueryKeys.ofId(nanoId),
			);
			const isSharedOrPublished =
				(branchItem && navbarBranchIsSharedOrPublished(branchItem)) ||
				Boolean(whiteboardCache?.publication?.isPublished) ||
				Boolean(whiteboardCache?.collaboration?.isShared);

			if (isSharedOrPublished) {
				openArchiveBlockedModal(whiteboardDocId);
				return;
			}

			const isCurrentWhiteboard = isActiveWhiteboardItem(
				activeWhiteboardId,
				itemId,
			);
			const isArchived = await archiveWhiteboard(itemId);

			if (isArchived && isCurrentWhiteboard) {
				navigateAwayFromCurrentBoard(itemId);
			}
		},
		[
			activeWhiteboardId,
			archiveWhiteboard,
			removeNavbarCard,
			cardRouteMatch?.params.cardId,
			navigate,
			navigateAwayFromCurrentBoard,
			queryClient,
		],
	);

	const value = useMemo(
		() => ({
			onArchive: handleArchiveClick,
			onDuplicate: handleDuplicateClick,
			onLeave: handleLeaveClick,
		}),
		[handleArchiveClick, handleDuplicateClick, handleLeaveClick],
	);

	return (
		<NavBarHandlerContext.Provider value={value}>
			{children}
		</NavBarHandlerContext.Provider>
	);
}

export function useNavBarHandlers() {
	const context = useContext(NavBarHandlerContext);
	if (context === undefined) {
		throw new Error(
			"useNavBarHandlers must be used within a NavBarHandlerProvider",
		);
	}
	return context;
}
